import type { DefaultTheme } from "react-jss";

import config from "@/constants/config";

import {
  apollocinemaTheme,
  apolloFoodTruckTheme,
  blenderTheme,
  cityExpressTheme,
  forumcinemasTheme,
  kfcTheme,
  mySushiTheme,
  olearysTheme,
  urbanKitchenTheme,
  vapianoTheme,
} from "./themes";
import type {
  ThemeCountry,
  ThemeCountryMap,
  ThemeKey,
  ThemeVariant,
  ThemeVariantMap,
} from "./types";

const selectThemeVariant = <T>(
  theme: ThemeVariantMap<T>,
  color: ThemeVariant,
): T => {
  const themeColor = theme[color];

  if (!themeColor) {
    throw new Error(
      `Theme color ${color} not found for theme ${JSON.stringify(theme)}`,
    );
  }

  return themeColor;
};

const selectThemeCountry = <T>(
  theme: ThemeCountryMap<T>,
  country: ThemeCountry,
): T => {
  const themeCountry = theme[country];

  if (!themeCountry) {
    throw new Error(
      `Theme country ${country} not found for theme ${JSON.stringify(theme)}`,
    );
  }

  return themeCountry;
};

const selectTheme = <T extends DefaultTheme>(
  theme:
    | ThemeCountryMap<T>
    | ThemeVariantMap<T>
    | ThemeCountryMap<ThemeVariantMap<T>>,
  country?: ThemeCountry,
  color?: ThemeVariant,
): DefaultTheme => {
  if (country && color) {
    const themeColors = selectThemeCountry(
      theme as ThemeCountryMap<ThemeVariantMap<T>>,
      country,
    );

    return selectThemeVariant(themeColors, color);
  }

  if (country) {
    return selectThemeCountry(theme as ThemeCountryMap<T>, country);
  }

  if (color) {
    return selectThemeVariant(theme as ThemeVariantMap<T>, color);
  }

  throw new Error(
    `Theme ${JSON.stringify(theme)} not found for country ${country} and color ${color}`,
  );
};

const applyThemeMode = (color: ThemeVariant): void => {
  document.body.style.backgroundColor =
    color === "DARK" ? "#101010" : "#FFFFFF";
};

type ThemeConfigsType = Record<
  ThemeKey,
  (themeVariant: ThemeVariant) => DefaultTheme
>;

const ThemeConfigs: ThemeConfigsType = {
  VAPIANO: (color) => selectThemeVariant(vapianoTheme, color),
  OLEARYS: (color) => selectThemeVariant(olearysTheme, color),
  BLENDER: (color) => selectThemeVariant(blenderTheme, color),
  MYSUSHI: (color) => selectThemeVariant(mySushiTheme, color),
  APOLLO_CINEMA_EE: (color) => selectTheme(apollocinemaTheme, "EE", color),
  APOLLO_CINEMA_LT: (color) => selectTheme(apollocinemaTheme, "LT", color),
  FORUM_CINEMAS_LT: (color) => selectThemeVariant(forumcinemasTheme, color),
  KFC: (color) => selectThemeVariant(kfcTheme, color),
  URBAN_KITCHEN: (color) => selectThemeVariant(urbanKitchenTheme, color),
  CITY_EXPRESS: (color) => selectThemeVariant(cityExpressTheme, color),
  APOLLO_FOOD_TRUCK: (color) => selectThemeVariant(apolloFoodTruckTheme, color),
} as const;

export const getBrandTheme = (): DefaultTheme => {
  const theme = config.themeConfig.key;
  const defaultThemeVariant = config.themeConfig.defaultVariant;

  applyThemeMode(defaultThemeVariant);
  const themeConfig = ThemeConfigs[theme];

  if (!themeConfig) throw new Error(`ThemeConfig not found for brand ${theme}`);

  return themeConfig(defaultThemeVariant);
};

export const getBlobStorage = () => {
  const { blobStorage } = getBrandTheme();
  return blobStorage;
};
